/**
 * Toast Notification System
 */
import { useEffect, useState, useCallback } from 'react'
import { create } from 'zustand'
import './Toast.css'

export type ToastSeverity = 'critical' | 'high' | 'medium' | 'low' | 'success' | 'info'

export interface ToastMessage {
  id: string
  severity: ToastSeverity
  title: string
  body?: string
  ticker?: string
  duration?: number // ms, 0 = no auto-dismiss
  action?: {
    label: string
    onClick: () => void
  }
}

interface ToastState {
  toasts: ToastMessage[]
  addToast: (toast: Omit<ToastMessage, 'id'>) => string
  removeToast: (id: string) => void
  clearAll: () => void
}

// Zustand store for toast state
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }]
    }))
    return id
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id)
    }))
  },

  clearAll: () => set({ toasts: [] })
}))

// Helper function to show toast from anywhere
export function showToast(toast: Omit<ToastMessage, 'id'>): string {
  return useToastStore.getState().addToast(toast)
}

// Icons for severity
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
)

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 18L18 6M6 6l12 12" />
  </svg>
)

function getSeverityIcon(severity: ToastSeverity) {
  switch (severity) {
    case 'critical':
    case 'high':
      return <AlertIcon />
    case 'medium':
      return <WarningIcon />
    case 'success':
      return <SuccessIcon />
    default:
      return <InfoIcon />
  }
}

function getDefaultDuration(severity: ToastSeverity): number {
  switch (severity) {
    case 'critical':
      return 0 // Don't auto-dismiss critical
    case 'high':
      return 8000
    case 'success':
      return 3000
    default:
      return 5000
  }
}

// Individual Toast Component
interface ToastItemProps {
  toast: ToastMessage
  onRemove: (id: string) => void
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false)
  const duration = toast.duration ?? getDefaultDuration(toast.severity)

  const handleRemove = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => onRemove(toast.id), 200)
  }, [toast.id, onRemove])

  // Auto-dismiss
  useEffect(() => {
    if (duration === 0) return

    const timer = setTimeout(handleRemove, duration)
    return () => clearTimeout(timer)
  }, [duration, handleRemove])

  return (
    <div
      className={`toast toast--${toast.severity} ${isExiting ? 'exiting' : ''}`}
      role="alert"
      aria-live={toast.severity === 'critical' ? 'assertive' : 'polite'}
    >
      <div className="toast__icon">{getSeverityIcon(toast.severity)}</div>

      <div className="toast__content">
        <div className="toast__title">
          {toast.title}
          {toast.ticker && <span className="toast__ticker">{toast.ticker}</span>}
        </div>
        {toast.body && <div className="toast__body">{toast.body}</div>}
        {toast.action && (
          <div className="toast__action">
            <button
              className="toast__action-btn"
              onClick={() => {
                toast.action?.onClick()
                handleRemove()
              }}
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>

      <button
        className="toast__close"
        onClick={handleRemove}
        aria-label="Dismiss notification"
      >
        <CloseIcon />
      </button>

      {duration > 0 && (
        <div
          className="toast__progress"
          style={{ animationDuration: `${duration}ms` }}
        />
      )}
    </div>
  )
}

// Toast Container Component
export function ToastContainer() {
  const { toasts, removeToast } = useToastStore()

  if (toasts.length === 0) return null

  return (
    <div className="toast-container" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  )
}

export default ToastContainer
